/**
 * Query keys.
 *
 * Every key carries `orgId` where the resource is org-scoped, so a future org switcher
 * invalidates correctly by construction rather than by someone remembering to.
 */
export const qk = {
  me: () => ["me"] as const,
  subscription: (orgId: string) => ["subscription", orgId] as const,
  entitlements: () => ["entitlements"] as const, // self-scoped by the token
  members: (orgId: string) => ["members", orgId] as const,
  invites: (orgId: string) => ["invites", orgId] as const,
  policy: (orgId: string) => ["policy", orgId] as const,
  policyAudit: (orgId: string, page: string | null) => ["policy", orgId, "audit", page] as const,
  policyVersion: (orgId: string, version: number) => ["policy", orgId, "version", version] as const,
  keys: (orgId: string) => ["keys", orgId] as const,
  invoices: (orgId: string) => ["invoices", orgId] as const, // infinite, cursor-paged
  usage: () => ["usage"] as const, // self-scoped by the token
} as const;

/** Cache policy, in one table, from the API-binding spec. */
export const CACHE = {
  /**
   * staleTime Infinity WITH refetchOnWindowFocus — deliberately. It should not refetch on
   * every mount, but it must refetch when the user comes back, because that is when a role
   * change made by a colleague becomes visible.
   */
  me: { staleTime: Infinity, refetchOnWindowFocus: true },
  subscription: { staleTime: 60_000, refetchOnWindowFocus: true },
  entitlements: { staleTime: 60_000, refetchOnWindowFocus: true },
  members: { staleTime: 30_000, refetchOnWindowFocus: true },
  invites: { staleTime: 30_000, refetchOnWindowFocus: true },
  policy: { staleTime: 60_000, refetchOnWindowFocus: true },
  keys: { staleTime: 60_000, refetchOnWindowFocus: true },
  invoices: { staleTime: 300_000, refetchOnWindowFocus: true },
  /**
   * The least important data on the screen. No focus refetch, no retry, no polling — one
   * attempt, then the honest degraded state.
   */
  usage: { staleTime: 300_000, refetchOnWindowFocus: false, retry: 0 },
} as const;

/** Poll schedules. None is a bare interval; each has an explicit stop condition. */
export const POLL = {
  /** every 5s, stop at 2 minutes */
  verifyEmail: { intervalMs: 5_000, ceilingMs: 120_000 },
  /** 1s, 2s, 3s, 5s, 8s — about 20s total, then state C */
  checkoutSuccess: [1_000, 2_000, 3_000, 5_000, 8_000] as const,
  /** every 3s, stop at 60s */
  incomplete: { intervalMs: 3_000, ceilingMs: 60_000 },
} as const;
